import React from 'react'

import { Switch, Textarea } from '@clientbase/clientbase-library'
import InfoIcon from '@mui/icons-material/Info'
import { Box, Stack, Tooltip, Typography } from '@mui/material'

import { NfseDialogMode } from '../../BodyModalNewChargeOrRecurrence.edit.utils'

export type NfseEmitChoice = 'now' | 'policy'

export interface NfseReissuePermissions {
  canCancel: boolean
  canManage: boolean
  canEmit: boolean
}

export interface NfseReissueFieldsProps {
  mode: NfseDialogMode
  confirmedNfseNumber?: string | null
  permissions: NfseReissuePermissions
  // Passo 2 da nota usa 2 toggles: reemitir (cancela+emite) e cancelar (anula).
  reemit: boolean
  onReemitChange: (value: boolean) => void
  cancelOld: boolean
  onCancelOldChange: (value: boolean) => void
  justificativa: string
  onJustificativaChange: (value: string) => void
  emitChoice: NfseEmitChoice
  onEmitChoiceChange: (value: NfseEmitChoice) => void
}

const InfoBox: React.FC<{
  children: React.ReactNode
  tone?: 'warn' | 'info'
}> = ({ children, tone = 'info' }) => (
  <Box
    sx={{
      fontSize: 13,
      py: 1,
      px: 1.5,
      backgroundColor: '#FAFBFA',
      borderRadius: '6px',
      borderLeft: `3px solid ${tone === 'warn' ? '#B26A00' : '#1F6F43'}`,
      color: 'text.primary',
      display: 'flex',
      gap: 0.75,
      alignItems: 'flex-start'
    }}
  >
    <InfoIcon
      sx={{
        fontSize: 16,
        mt: '2px',
        color: tone === 'warn' ? '#B26A00' : '#1F6F43'
      }}
    />
    <Typography variant="smLight" component="span">
      {children}
    </Typography>
  </Box>
)

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({
  children
}) => (
  <Typography variant="smBold" sx={{ display: 'block' }}>
    {children}
  </Typography>
)

// Caixa de toggle no mesmo padrão visual do "Notificar novamente o cliente",
// com tooltip opcional no título e conteúdo expansível abaixo (ex.: justificativa).
const ToggleRow: React.FC<{
  title: string
  subtitle: string
  checked: boolean
  onChange: (value: boolean) => void
  disabled?: boolean
  infoTooltip?: string
  children?: React.ReactNode
}> = ({
  title,
  subtitle,
  checked,
  onChange,
  disabled,
  infoTooltip,
  children
}) => (
  <Box
    sx={{
      backgroundColor: '#FAFBFA',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: '8px'
    }}
  >
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{ py: 1.25, px: 1.5 }}
    >
      <Box sx={{ flex: 1, pr: 1 }}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography variant="smBold" lineHeight={1.2}>
            {title}
          </Typography>
          {infoTooltip && (
            <Tooltip
              arrow
              placement="top"
              componentsProps={{
                tooltip: { sx: { fontSize: 12.5, lineHeight: 1.5 } }
              }}
              title={infoTooltip}
            >
              <InfoIcon
                sx={{ fontSize: 16, color: 'text.secondary', display: 'block' }}
              />
            </Tooltip>
          )}
        </Stack>
        <Typography variant="smLight" color="text.secondary" display="block">
          {subtitle}
        </Typography>
      </Box>
      <Switch
        checked={checked}
        disabled={disabled}
        onChange={(_, value) => onChange(value)}
      />
    </Stack>
    {children && <Box sx={{ px: 1.5, pt: 2.5, pb: 1.5 }}>{children}</Box>}
  </Box>
)

const NfseReissueFields: React.FC<NfseReissueFieldsProps> = ({
  mode,
  confirmedNfseNumber,
  permissions,
  reemit,
  onReemitChange,
  cancelOld,
  onCancelOldChange,
  justificativa,
  onJustificativaChange,
  emitChoice,
  onEmitChoiceChange
}) => {
  const { canCancel, canEmit } = permissions

  if (mode === 'blocked') {
    return (
      <InfoBox tone="warn">
        A nota fiscal ainda está em processamento na prefeitura. Aguarde a
        autorização para poder cancelar e reemitir. Nenhuma ação de nota será
        feita agora.
      </InfoBox>
    )
  }

  if (mode === 'reissue') {
    // Cancelar e reemitir exigem permissão de cancelamento. Sem ela, degrada.
    if (!canCancel) {
      return (
        <InfoBox tone="warn">
          Você não tem permissão para cancelar ou reemitir notas fiscais. A
          alteração será salva, mas a nota emitida não será atualizada.
        </InfoBox>
      )
    }

    return (
      <Stack direction="column" spacing={1.5} width="100%">
        <SectionTitle>
          Nota fiscal{confirmedNfseNumber ? ` nº ${confirmedNfseNumber}` : ''}{' '}
          ficará desatualizada. O que fazer?
        </SectionTitle>

        <ToggleRow
          title="Cancelar a nota atual"
          subtitle={
            reemit
              ? 'Incluído na reemissão'
              : 'Anula a nota na prefeitura, sem emitir outra'
          }
          checked={cancelOld || reemit}
          disabled={reemit}
          onChange={onCancelOldChange}
        >
          {cancelOld && !reemit && (
            <Textarea
              label="Justificativa do cancelamento"
              style={{
                minHeight: '70px',
                resize: 'vertical',
                maxHeight: '50vh'
              }}
              placeholder="Motivo do cancelamento enviado à prefeitura"
              value={justificativa}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                onJustificativaChange(e.target.value)
              }
            />
          )}
        </ToggleRow>

        <ToggleRow
          title="Reemitir a nota atualizada"
          subtitle="A nota é reemitida automaticamente ao salvar"
          infoTooltip="Ao salvar, a nota é reemitida automaticamente com os dados atualizados desta cobrança. A nota antiga é cancelada nesse processo."
          checked={reemit}
          onChange={(value) => {
            onReemitChange(value)
            if (value) onCancelOldChange(false)
          }}
        />
      </Stack>
    )
  }

  if (mode === 'emit') {
    if (!canEmit) {
      return (
        <InfoBox tone="warn">
          Você não tem permissão para emitir notas fiscais. A configuração da
          nota será salva, mas nenhuma nota será emitida agora.
        </InfoBox>
      )
    }

    return (
      <Stack direction="column" spacing={1.5} width="100%">
        <SectionTitle>
          Esta cobrança ainda não tem nota fiscal. O que fazer?
        </SectionTitle>

        <ToggleRow
          title="Emitir a nota"
          subtitle={
            emitChoice === 'now'
              ? 'A nota é emitida automaticamente ao salvar'
              : 'A configuração emitirá a nota automaticamente depois'
          }
          infoTooltip="Ao salvar, a nota é emitida automaticamente com os dados desta cobrança."
          checked={emitChoice === 'now'}
          onChange={(value) => onEmitChoiceChange(value ? 'now' : 'policy')}
        />
      </Stack>
    )
  }

  return null
}

export default NfseReissueFields
